<template>
  <div>
  <div
    v-if="vehicleView"
    class="booking-list-card-vehicle-line"
    @click="$emit('open-booking-details', booking)"
  >
    <i
      v-if="missingIdentity"
      class="fas fa-exclamation-circle booking-list-card-vehicle-alert"
      title="Documento ou nome completo ausente"
      aria-label="Documento ou nome completo ausente"
    ></i>
    <strong>#{{ booking.code }}</strong>
    <span> - {{ customerName }} ({{ paxLabel }})</span>
  </div>
  <div
    v-else
    class="booking-list-card-wrapper"
    :class="{ 'booking-list-card-wrapper--compact': isCompactLayout }"
  >
    <div
      class="booking-list-card-header"
      :style="{ backgroundColor: cardHeaderColor }"
    >
      <span>{{ cardHeaderLabel }}</span>
    </div>
    <div
      class="booking-list-card"
      @click="$emit('open-booking-details', booking)"
      style="cursor: pointer"
    >
      <b-row>
        <b-col
          cols="12"
          :md="isCompactLayout ? 12 : 6"
          class="booking-list-card-col"
        >
          <div class="booking-list-card-top">
            <div class="booking-list-card-title">
              <strong>#{{ booking.code }}</strong>
              <span class="booking-list-card-product">
                <span v-if="bookingProduct">| {{ bookingProduct }}</span>
                <span
                  v-if="bookingPlace"
                  class="booking-list-card-product-place"
                >
                  - {{ bookingPlace }}</span
                >
              </span>
            </div>
          </div>

          <div class="booking-list-card-customer">
            {{ customerLabel }}
          </div>

          <div v-if="!isCompactLayout" class="booking-list-card-row">
            <strong>Agendado para:</strong>
            <span>{{ scheduleLabel }}</span>
          </div>
        </b-col>

        <b-col
          cols="12"
          :md="isCompactLayout ? 12 : 6"
          class="booking-list-card-col"
        >
          <div
            v-if="!isCompactLayout"
            class="booking-list-card-row booking-list-card-payment"
          >
            <strong>Pagamento:</strong>
            <span>
              <template v-if="paymentSummary">
                {{ paymentSummary }}
              </template>
              <template v-if="!paymentSummary && !hasLocalPaymentPending">
                {{
                  formatCurrency(
                    booking.finalValue != null
                      ? booking.finalValue
                      : booking.valor
                  )
                }}
              </template>
            </span>
          </div>

          <div
            v-if="conductorLabel"
            class="booking-list-card-row booking-list-card-agent"
          >
            <strong>Condução:</strong>
            <span>{{ conductorLabel }}</span>
          </div>
          <div class="booking-list-card-row booking-list-card-checkin-text">
            <span v-if="hasCheckin" class="booking-list-card-checkin-done">
              utilizado
              <span
                v-if="checkinOperator"
                class="booking-list-card-checkin-operator"
              >
                ({{ checkinOperator }})
              </span>
            </span>
            <span v-else class="booking-list-card-checkin-waiting"
              >aguardando</span
            >
          </div>

          <div
            v-if="showTransport"
            class="booking-list-card-row booking-list-card-transport"
          >
            <span
              :class="{ 'booking-list-card-checkin-waiting': !transportName }"
            >
              {{ transportName || 'transporte não definido' }}
            </span>
          </div>

          <div class="booking-list-card-row booking-list-card-checkin"></div>
        </b-col>
      </b-row>
    </div>
  </div>
  </div>
</template>

<script>
import moment from 'moment'
import {
  bookingOwnerName,
  isMissingFullName,
  isMissingIdentityDocument,
} from './bookingIdentity'

const SM_MEDIA_QUERY = '(max-width: 767.98px)'

export default {
  name: 'BookingListCard',

  props: {
    booking: {
      type: Object,
      required: true,
    },

    /**
     * Força o layout compacto (sm), mesmo em telas md+.
     */
    compact: {
      type: Boolean,
      default: false,
    },

    vehicleView: {
      type: Boolean,
      default: false,
    },

    showTransport: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isSmViewport: false,
      mediaQueryList: null,
    }
  },

  computed: {
    isCompactLayout() {
      return this.compact || this.isSmViewport
    },

    conductorLabel() {
      return (this.booking.agents || [])
        .filter((agent) => agent && agent.active)
        .map((agent) => this.formatAgentDisplayName(agent))
        .filter(Boolean)
        .join(', ')
    },

    transportName() {
      return (this.booking.vehicles || [])
        .filter((item) => item && item.active)
        .map((item) => item.name || item.plate)
        .filter(Boolean)
        .join(', ')
    },

    isBookingPaid() {
      const payments = this.booking.payments || []
      let hasFreeBooking = false
      const totalPaid = payments.reduce((acc, payment) => {
        if (payment.payment_status === 'APPROVED') {
          if (
            payment.data_origin &&
            payment.data_origin['Name'] &&
            payment.data_origin['Name'].toUpperCase() === 'CORTESIA'
          ) {
            hasFreeBooking = true
          }
          return acc + Number(payment.payment_value)
        }
        return acc
      }, 0)

      return totalPaid >= this.booking.finalValue || hasFreeBooking
    },

    hasCheckin() {
      if (this.booking.checkin && this.booking.checkin.hasCheckin) return true
      if (this.booking.checkins && this.booking.checkins.length > 0) return true
      if (
        this.booking.checkin &&
        this.booking.checkin.list &&
        this.booking.checkin.list.length > 0
      )
        return true
      return false
    },

    cardHeaderLabel() {
      const status = String(this.booking.status || '').toUpperCase()
      if (status === 'CANCELLED') return 'cancelada'
      if (status === 'APPROVED' && !this.isBookingPaid)
        return this.compact
          ? 'pré-reserva'
          : 'Pré-reserva (aguardando pagamento)'
      if (!this.isBookingPaid)
        return this.isCompactLayout ? 'cotação' : 'Cotação'
      if (this.hasCheckin)
        return this.isCompactLayout ? 'utilizado' : 'Ingresso utilizado'
      return this.isCompactLayout ? 'sem check-in' : 'Aguardando check-in'
    },

    cardHeaderColor() {
      const status = String(this.booking.status || '').toUpperCase()
      if (status === 'CANCELLED') return '#D93051'
      if (status === 'APPROVED' && !this.isBookingPaid) return '#E67E22'
      if (!this.isBookingPaid) return '#999'
      if (this.hasCheckin) return 'rgb(32, 155, 103)'
      return '#1A6884'
    },

    bookingPlace() {
      return (
        (this.booking.poi &&
          (this.booking.poi.place || this.booking.poi.product_place_name)) ||
        ''
      )
    },

    bookingProduct() {
      let productName = (this.booking.poi && this.booking.poi.name) || ''
      productName = productName
        .replace('Passeio turístico - ', '')
        .replace('Experiência - ', '')
        .replace('passeio ', '')
        .replace(' (BT)', '')
      return productName || ''
    },

    customerLabel() {
      let peopleText =
        this.booking.people &&
        this.booking.people.tableText &&
        this.booking.people.tableText.line1
          ? this.booking.people.tableText.line1
          : 'nome não informado'
      peopleText = peopleText
        .replace('Visitante (', '')
        .replace('(0 pessoa)', 'nenhuma pessoa')
        .trim()
      return peopleText || 'nome não informado'
    },

    customerName() {
      return bookingOwnerName(this.booking) || 'nome não informado'
    },

    missingCpf() {
      return isMissingIdentityDocument(this.booking)
    },

    missingFullName() {
      return isMissingFullName(this.customerName)
    },

    missingIdentity() {
      return this.missingCpf || this.missingFullName
    },

    paxCount() {
      const table = this.booking.people && this.booking.people.tableText
      const count = table && Number(table.count)
      return Number.isFinite(count) ? count : 0
    },

    paxLabel() {
      return this.paxCount === 1 ? '1 pessoa' : `${this.paxCount} pessoas`
    },

    scheduleLabel() {
      if (!this.booking.data) return '--'
      const date = moment(this.booking.data)
      if (!date.isValid()) return '--'
      return `${date.format('D MMM').toUpperCase()}, ${date
        .format('HH[h]mm')
        .replace('00', '')}`
    },

    paymentSummary() {
      const payments = this.booking.payments || []
      const approved = payments.filter((payment) => {
        const status = String(payment.payment_status || '').toUpperCase()
        const method = String(payment.payment_method || '').toLowerCase()
        const isLocalPending =
          method.indexOf('cod') === 0 ||
          status === 'PENDING' ||
          status === 'UNPAID'
        return !isLocalPending && Number(payment.payment_value) > 0
      })

      if (approved.length > 0) {
        return approved
          .map((payment) => {
            const label =
              payment.payment_description ||
              payment.payment_method ||
              'Pagamento'
            return `${label}: ${this.formatCurrency(payment.payment_value)}`
          })
          .join(' ')
      }

      if (
        this.booking.paymentDescription &&
        Number(this.booking.valor) >= 0 &&
        this.isBookingPaid
      ) {
        return `${this.booking.paymentDescription}: ${this.formatCurrency(
          this.booking.finalValue != null
            ? this.booking.finalValue
            : this.booking.valor
        )}`
      }

      return ''
    },

    hasLocalPaymentPending() {
      if (
        this.booking.confirmPayment &&
        this.booking.confirmPayment.hasLocalPayment
      )
        return true

      const payments = this.booking.payments || []
      return payments.some((payment) => {
        const method = String(payment.payment_method || '').toLowerCase()
        const status = String(payment.payment_status || '').toUpperCase()
        return (
          method.indexOf('cod') === 0 ||
          status === 'PENDING' ||
          status === 'UNPAID'
        )
      })
    },

    checkinOperator() {
      if (
        this.booking.checkin &&
        this.booking.checkin.list &&
        this.booking.checkin.list.length > 0
      ) {
        const item = this.booking.checkin.list[0]
        return item.operator || item.name || ''
      }

      if (this.booking.checkins && this.booking.checkins.length > 0) {
        const item = this.booking.checkins[0]
        return item.operator || item.name || ''
      }

      if (
        Array.isArray(this.booking.checkin) &&
        this.booking.checkin.length > 0
      ) {
        const item = this.booking.checkin[0]
        return item.operator || item.name || ''
      }

      return ''
    },
  },

  mounted() {
    if (typeof window === 'undefined' || !window.matchMedia) return
    this.mediaQueryList = window.matchMedia(SM_MEDIA_QUERY)
    this.isSmViewport = this.mediaQueryList.matches
    if (this.mediaQueryList.addEventListener) {
      this.mediaQueryList.addEventListener('change', this.onViewportChange)
    } else if (this.mediaQueryList.addListener) {
      this.mediaQueryList.addListener(this.onViewportChange)
    }
  },

  beforeDestroy() {
    if (!this.mediaQueryList) return
    if (this.mediaQueryList.removeEventListener) {
      this.mediaQueryList.removeEventListener('change', this.onViewportChange)
    } else if (this.mediaQueryList.removeListener) {
      this.mediaQueryList.removeListener(this.onViewportChange)
    }
  },

  methods: {
    onViewportChange(event) {
      this.isSmViewport = event.matches
    },

    formatAgentDisplayName(agent) {
      if (!agent || !agent.name) return ''
      const nickname =
        agent.nickname != null ? String(agent.nickname).trim() : ''
      return nickname ? `${agent.name} (${nickname})` : agent.name
    },

    formatCurrency(value) {
      return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
  },
}
</script>

<style scoped>
.booking-list-card-wrapper {
  border: 1px solid #d7dde3;
  border-radius: 1rem 0 0 1rem;
  overflow: hidden;
  background: #fff;
}

.booking-list-card-wrapper--compact {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  border-radius: 12px;
}

.booking-list-card {
  background-color: #ffffff;
  height: auto;
  padding: 14px 16px 16px;
}

.booking-list-card-wrapper--compact .booking-list-card {
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
}

.booking-list-card-col {
  margin-bottom: 0;
}

@media (min-width: 768px) {
  .booking-list-card-wrapper:not(.booking-list-card-wrapper--compact)
    .booking-list-card-col:first-child {
    padding-right: 12px;
  }

  .booking-list-card-wrapper:not(.booking-list-card-wrapper--compact)
    .booking-list-card-col:last-child {
    padding-left: 12px;
    border-left: 1px solid #eef1f4;
  }

  .booking-list-card-wrapper:not(.booking-list-card-wrapper--compact)
    .booking-list-card-customer {
    margin-bottom: 6px;
  }
}

.booking-list-card-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 28px;
  padding: 0 1rem 0 0.1rem;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  text-transform: lowercase;
}

.booking-list-card-wrapper--compact .booking-list-card-header {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  justify-content: center;
  align-items: center;
  min-height: auto;
  min-width: 28px;
  max-width: 36px;
  padding: 10px 4px;
  flex-shrink: 0;
  text-align: center;
  line-height: 1.2;
}

.booking-list-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 6px;
}

.booking-list-card-title {
  color: #1f2a37;
  line-height: 1.35;
}

.booking-list-card-product {
  color: #5f6b7a;
  font-weight: 400;
  margin-left: 4px;
}

.booking-list-card-action {
  color: #12576e;
  cursor: pointer;
  line-height: 1;
}

.booking-list-card-action .no {
  font-size: 18px;
}

.booking-list-card-vehicle-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  cursor: pointer;
  padding: 0.35rem 0.75rem 0.35rem 0.5rem;
  border-radius: 0.35rem;
  color: #3b4654;
  font-size: 0.95rem;
}

.booking-list-card-vehicle-alert {
  color: #d93051;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.booking-list-card-vehicle-line:hover {
  background: rgba(18, 87, 110, 0.08);
}

.booking-list-card-customer {
  color: #3b4654;
  font-size: 14px;
  margin-bottom: 10px;
}

.booking-list-card-wrapper--compact .booking-list-card-customer {
  margin-bottom: 6px;
}

.booking-list-card-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  color: #3b4654;
  font-size: 14px;
  margin-bottom: 6px;
}

.booking-list-card-row strong {
  color: #1f2a37;
}

.booking-list-card-local {
  color: #d93051;
  font-weight: 700;
  margin-left: 4px;
}

.booking-list-card-checkin {
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  margin-top: 4px;
}

.booking-list-card-checkin-text {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
}

.booking-list-card-checkin-waiting {
  color: #9aa3af;
  font-style: italic;
}

.booking-list-card-checkin-done {
  color: #22a06b;
  font-weight: 700;
}

.booking-list-card-checkin-operator {
  color: #9aa3af;
  font-weight: 400;
  margin-left: 2px;
}

.booking-list-card-checkin-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #e8ebdf;
  color: #8a9178;
  cursor: pointer;
}

.booking-list-card-checkin-icon.done {
  background: #e5efd0;
  color: #6f9b2c;
}

.booking-list-card-checkin-icon .no {
  font-size: 16px;
}
</style>
